import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, Mail, Phone, Globe } from "lucide-react";
import { RESUME, SITE } from "../mock";
import Seo from "../components/Seo";

const RESUME_PDF_URL = "/images/resume.pdf";

const SectionHeading = ({ children }) => (
  <div className="mb-5">
    <h2 className="font-display text-2xl md:text-3xl tracking-[0.08em] text-[#EDE3CB] uppercase">{children}</h2>
    <div className="brass-divider mt-2 opacity-70" />
  </div>
);

const Resume = () => {
  return (
    <div className="bg-[#0E0E0F] text-[#EDE3CB]">
      <Seo
        path="/resume"
        title="Resume"
        description="Property Master credits: Passenger (Paramount), The Actor Awards (Netflix), Whitney Springs (Paramount), The Parenting (HBO Max), Rockbottom, The Binge 2 (Hulu), and more. 15+ years across studio films, streaming series, and television."
      />
      {/* Top header bar — modeled on the actual resume */}
      <section className="bg-[#0B0B0C] border-b border-[#B8935A]/40 relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/3 opacity-[0.08] pointer-events-none">
          <div className="h-full w-full bg-[radial-gradient(circle_at_right,_#B8935A_0%,_transparent_60%)]" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12 py-12 lg:py-14">
          <h1 className="font-display text-5xl md:text-7xl tracking-[0.04em] leading-none">
            JESSE J. <span className="brass-text">ADAMS</span>
          </h1>
          <div className="mt-3 text-sm md:text-base tracking-[0.28em] uppercase text-[#EDE3CB]/85 font-medium">
            IATSE Local 44 — Property Master
          </div>
          <div className="brass-divider mt-6 max-w-2xl opacity-50" />
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-x-10 gap-y-3 text-sm">
            <a href={`tel:${SITE.phone}`} className="flex items-center gap-2 text-[#EDE3CB]/90 hover:text-[#B8935A] transition-colors">
              <Phone size={14} className="text-[#B8935A]" /> {SITE.phone}
            </a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 text-[#EDE3CB]/90 hover:text-[#B8935A] transition-colors">
              <Mail size={14} className="text-[#B8935A]" /> {SITE.email}
            </a>
            <span className="flex items-center gap-2 text-[#EDE3CB]/90">
              <Globe size={14} className="text-[#B8935A]" /> www.propmeister.art
            </span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={RESUME_PDF_URL}
              target="_blank"
              rel="noreferrer"
              download="Jesse-J-Adams-Resume.pdf"
              data-testid="resume-pdf-download-btn"
              className="btn-primary"
            >
              <Download size={14} /> Resume PDF
            </a>
          </div>
        </div>
      </section>

      {/* Two-column body */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-x-14 gap-y-16">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-7 space-y-14">
          {/* Professional Summary */}
          <div>
            <SectionHeading>Professional Summary</SectionHeading>
            <div className="space-y-5 text-[#EDE3CB]/85 text-[15px] leading-[1.75]">
              {RESUME.summaryParas.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          {/* Property Department Experience */}
          <div>
            <SectionHeading>Property Department Experience</SectionHeading>
            <div className="space-y-10">
              {RESUME.experience.map((group) => (
                <div key={group.heading}>
                  <div className="text-[11px] tracking-[0.32em] uppercase text-[#B8935A] font-medium mb-5">
                    {group.heading}
                  </div>
                  <div className="space-y-5">
                    {group.items.map((c, i) => (
                      <div key={i} className="border-b border-white/8 pb-5 last:border-b-0">
                        <div className="font-serif-accent text-[20px] md:text-[22px] leading-snug text-[#EDE3CB]">
                          {(c.link || c.imdb) ? (
                            <a
                              href={c.link || c.imdb}
                              target="_blank"
                              rel="noreferrer"
                              className="font-semibold underline underline-offset-4 decoration-[#B8935A]/35 decoration-1 hover:text-[#B8935A] hover:decoration-[#B8935A] transition-colors"
                            >
                              {c.title}
                            </a>
                          ) : (
                            <span className="font-semibold">{c.title}</span>
                          )}
                          <span className="text-[#EDE3CB]/75"> ({c.studio})</span>
                          {c.director && (
                            <>
                              <span className="text-[#EDE3CB]/75"> — {c.director} </span>
                              <span className="text-[#B8935A] text-base align-middle">(DR)</span>
                            </>
                          )}
                        </div>
                        <div className="mt-1.5 text-[12px] tracking-[0.18em] uppercase text-[#EDE3CB]/55">
                          {c.type} <span className="text-[#B8935A]/70 mx-2">/</span> {c.year} <span className="text-[#B8935A]/70 mx-2">/</span> {c.role}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-5 space-y-14">
          {/* Skills */}
          <div>
            <SectionHeading>Skills</SectionHeading>
            <div className="space-y-6">
              {RESUME.skills.map((s) => (
                <div key={s.name}>
                  <div className="font-serif-accent font-semibold text-[18px] text-[#EDE3CB] leading-tight">{s.name}</div>
                  <p className="mt-2 text-[14px] leading-[1.7] text-[#EDE3CB]/75">{s.body}</p>
                  <div className="brass-divider mt-5 opacity-25" />
                </div>
              ))}
            </div>
          </div>

          {/* Archival Work */}
          <div>
            <SectionHeading>Archival Work</SectionHeading>
            <div className="font-serif-accent font-semibold text-[18px] text-[#EDE3CB]">{RESUME.archival.org}</div>
            <p className="mt-3 text-[14px] leading-[1.7] text-[#EDE3CB]/80">{RESUME.archival.body}</p>
            <ul className="mt-3 space-y-1.5 text-[14px] text-[#EDE3CB]/80">
              {RESUME.archival.items.map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="text-[#B8935A] mt-2 w-1 h-1 rounded-full bg-[#B8935A] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Affiliations */}
          <div>
            <SectionHeading>Affiliations</SectionHeading>
            <div className="space-y-5">
              {RESUME.affiliations.map((a) => (
                <div key={a.org} className="border-b border-white/8 pb-4 last:border-b-0">
                  <div className="font-serif-accent font-semibold text-[18px] text-[#EDE3CB] leading-snug">{a.org}</div>
                  <div className="mt-1 text-[12px] tracking-[0.22em] uppercase text-[#B8935A]">{a.role}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 pb-20">
        <div className="border border-[#B8935A]/30 p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="section-label">Working a project?</div>
            <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">Send the dates. <span className="brass-text">I'll send the kit.</span></h3>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/contact" className="btn-primary">Check Availability <ArrowUpRight size={14} /></Link>
            <Link to="/" className="btn-secondary">View Recent Work</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resume;
